import React from 'react';
import { Link } from 'react-router-dom';
import { stores } from '../data/mockData';

const StoreLogos = () => {
  // Store logo text/images mapping with styling
  const storeLogos = {
    aliexpress: { text: 'AliExpress', color: '#e62e04', bg: '#fff', font: 'font-bold text-lg md:text-xl' },
    asos: { text: 'asos', color: '#2d2d2d', bg: '#fff', font: 'font-bold text-2xl md:text-3xl lowercase' },
    ebay: { text: 'ebay', colors: ['#e53238', '#0064d2', '#f5af02', '#86b817'], bg: '#fff' },
    gogreen: { text: 'Go Green', color: '#228b22', bg: '#fff', hasIcon: true },
    iherb: { text: 'iHerb', color: '#68a648', bg: '#fff', font: 'font-bold text-xl md:text-2xl' },
    laroche: { text: 'LA ROCHE-POSAY', color: '#0066b2', bg: '#fff', subtitle: 'LABORATOIRE DERMATOLOGIQUE', font: 'font-bold text-sm md:text-base' },
    panasonic: { text: 'Panasonic', color: '#0066b2', bg: '#fff', font: 'font-bold text-xl md:text-2xl' },
    salamander: { text: 'SALAMANDER', color: '#ff6b00', bg: '#fff', font: 'font-bold text-base md:text-lg tracking-wide' },
    vichy: { text: 'VICHY', color: '#004976', bg: '#fff', subtitle: 'LABORATOIRES', font: 'font-bold text-xl md:text-2xl' },
    'yves-roche': { text: 'YVES ROCHER', color: '#006633', bg: '#fff', subtitle: 'FRANCE', font: 'font-bold text-base md:text-lg' },
    amazon: { text: 'amazon', color: '#ff9900', bg: '#232f3e', textColor: '#fff', font: 'font-bold text-xl md:text-2xl', arrow: true },
    walmart: { text: 'Walmart', color: '#0071ce', bg: '#fff', font: 'font-bold text-xl md:text-2xl', spark: true },
    target: { text: 'Target', color: '#cc0000', bg: '#fff', font: 'font-bold text-xl md:text-2xl', bullseye: true },
    bestbuy: { text: 'Best Buy', color: '#0046be', bg: '#fff1', font: 'font-bold text-lg md:text-xl', tag: true },
    // Fashion stores
    zara: { text: 'ZARA', color: '#1a1a1a', bg: '#fff', font: 'font-bold text-2xl md:text-3xl tracking-[0.2em]' },
    hm: { text: 'H&M', color: '#e50010', bg: '#fff', font: 'font-bold text-2xl md:text-3xl' },
    nike: { text: 'NIKE', color: '#1a1a1a', bg: '#fff', font: 'font-bold text-xl md:text-2xl tracking-wide', swoosh: true },
    nordstrom: { text: 'NORDSTROM', color: '#1a1a1a', bg: '#fff', font: 'font-medium text-sm md:text-base tracking-[0.15em]' },
    shein: { text: 'SHEIN', color: '#1a1a1a', bg: '#fff', font: 'font-bold text-xl md:text-2xl tracking-wide' },
    // Cosmetics stores
    sephora: { text: 'SEPHORA', color: '#1a1a1a', bg: '#fff', font: 'font-bold text-lg md:text-xl tracking-[0.1em]' },
    ulta: { text: 'ULTA', color: '#000', bg: '#fff', font: 'font-extrabold text-xl md:text-2xl', subtitle: 'beauty', subtitleColor: '#ff6900' },
    mac: { text: 'M·A·C', color: '#1a1a1a', bg: '#fff', font: 'font-bold text-2xl md:text-3xl tracking-wide' },
    'charlotte-tilbury': { text: 'Charlotte Tilbury', color: '#c9a961', bg: '#fff', font: 'font-serif text-lg md:text-xl italic' },
    glossier: { text: 'Glossier', color: '#000', bg: '#fff', font: 'font-medium text-xl md:text-2xl italic' },
  };

  const renderStoreLogo = (store) => {
    const logoInfo = storeLogos[store.slug];
    if (!logoInfo) {
      return <span className="text-lg font-bold text-gray-700">{store.name}</span>;
    }

    // eBay special rendering with colored letters
    if (store.slug === 'ebay') {
      return (
        <span className="text-2xl md:text-3xl font-bold">
          {'ebay'.split('').map((letter, i) => (
            <span key={i} style={{ color: logoInfo.colors[i] }}>{letter}</span>
          ))}
        </span>
      );
    }

    // Go Green with tree icon
    if (store.slug === 'gogreen') {
      return (
        <div className="flex flex-col items-center">
          <div className="text-gray-800 text-2xl md:text-3xl mb-1">🌲</div>
          <span className="text-base md:text-lg font-bold" style={{ color: logoInfo.color }}>Go Green</span>
        </div>
      );
    }

    // Amazon with arrow
    if (store.slug === 'amazon') {
      return (
        <div className="flex flex-col items-center">
          <span className={`${logoInfo.font}`} style={{ color: logoInfo.color }}>
            amazon
          </span>
          <div className="w-12 h-1 mt-0.5" style={{ 
            background: `linear-gradient(90deg, ${logoInfo.color} 70%, transparent 70%)`,
            clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)'
          }}></div>
        </div>
      );
    }

    // Walmart with spark
    if (store.slug === 'walmart') {
      return (
        <div className="flex items-center gap-1">
          <span className="text-yellow-500 text-xl">✦</span>
          <span className={`${logoInfo.font}`} style={{ color: logoInfo.color }}>Walmart</span>
        </div>
      );
    }

    // Target with bullseye
    if (store.slug === 'target') {
      return (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 md:w-8 md:h-8 rounded-full border-4 border-black flex items-center justify-center">
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-black"></div>
          </div>
          <span className={`${logoInfo.font}`} style={{ color: logoInfo.color }}>Target</span>
        </div>
      );
    }

    // Nike with swoosh effect
    if (store.slug === 'nike') {
      return (
        <div className="flex flex-col items-center">
          <svg viewBox="0 0 24 24" className="w-10 h-10 md:w-12 md:h-12 fill-black">
            <path d="M21 8.719L7.836 14.303C6.74 14.768 5.818 15 5.075 15c-.836 0-1.445-.295-1.819-.884-.485-.76-.273-1.982.559-3.272.494-.754 1.122-1.446 1.734-2.108-.144.234-.258.48-.322.718-.156.559-.123 1.064.129 1.503.373.65 1.108.982 2.182.982.57 0 1.223-.117 1.947-.351L21 8.719z"/>
          </svg>
        </div>
      );
    }

    // Ulta with beauty subtitle
    if (store.slug === 'ulta') {
      return (
        <div className="flex flex-col items-center">
          <span className={`${logoInfo.font}`} style={{ color: logoInfo.color }}>ULTA</span>
          <span className="text-xs md:text-sm font-bold" style={{ color: logoInfo.subtitleColor }}>beauty</span>
        </div>
      );
    }

    // Stores with subtitle
    if (logoInfo.subtitle && store.slug !== 'ulta') {
      return (
        <div className="flex flex-col items-center">
          <span className={`${logoInfo.font || 'text-lg md:text-xl font-bold'}`} style={{ color: logoInfo.color }}>
            {logoInfo.text}
          </span>
          <span className="text-[8px] md:text-[10px] text-gray-500 tracking-wider">{logoInfo.subtitle}</span>
        </div>
      );
    }

    // Default text logo
    return (
      <span
        className={`${logoInfo.font || 'text-xl md:text-2xl font-bold'}`}
        style={{ color: logoInfo.color }}
      >
        {logoInfo.text}
      </span>
    );
  };

  // Show mix of original and new stores (fashion/cosmetics)
  const displayStores = [
    ...stores.filter(s => ['amazon', 'walmart', 'target', 'ebay', 'asos'].includes(s.slug)),
    ...stores.filter(s => ['zara', 'nike', 'sephora', 'hm', 'mac'].includes(s.slug)),
  ].slice(0, 10);

  return (
    <section className="py-8 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 text-center">Popular Stores</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {displayStores.map((store, index) => (
            <Link
              key={store.id}
              to={`/store/${store.slug}`}
              className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 flex items-center justify-center min-h-[80px] md:min-h-[100px] hover:shadow-lg hover:border-[#000000] transition-all duration-300 card-hover animate-fadeInUp opacity-0"
              style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
            >
              {renderStoreLogo(store)}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StoreLogos;
